import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import {
  GetBannerItemDocument,
  type GetBannerItemQuery,
  GetFormDataDocument,
  type GetFormDataQuery,
  Locale,
} from '@seek/cmsu-cms-connect';
import type { SiteName } from '@seek/melways-sites';

import { getHygraphLocales } from '../client/shared/utils/helper';
import type { PreviewProps } from '../helpers/loadersHelper';
import { internalError } from '../responses';

type QueryProps = {
  client: ApolloClient<NormalizedCacheObject>;
  hygraphLocale: Locale;
  hygraphSite: SiteName;
};

// Get Contact US and Subscription Form for under "about" section
// Currently its being used for "about/news" section
// http://www.seek.com.au.local:8000/about/news/category/seek-news
// http://www.seek.com.au.local:8000/about/news/article/employment-dashboard-dec-2022
export const getAboutSubForm_ContactUs = async (
  section: string,
  queryProps: QueryProps,
  previewProps: PreviewProps,
) => {
  if (section === 'about') {
    const props = { ...queryProps, ...previewProps };
    const formData = await getSubscriptionForm(props);

    const contactUs = await getContactUs(props);
    return {
      formData,
      contactUs,
    };
  }
  return {
    formData: null,
    contactUs: null,
  };
};

const getSubscriptionForm = async ({
  hygraphLocale,
  hygraphSite,
  client,
  stage,
  queryOptions,
}: QueryProps & PreviewProps) => {
  // Hardcoded slug for subscribe form, need to create it in Hygraph Form Components
  const variables = {
    where: {
      slug: 'subscribe',
      sites_some: { name: hygraphSite },
    },
    first: 1,
    stage,
    locale: getHygraphLocales(hygraphLocale),
  };

  const { data, error } = await client.query<GetFormDataQuery>({
    query: GetFormDataDocument,
    variables,
    ...queryOptions,
  });

  if (error) {
    throw internalError(error);
  }
  if (Boolean(data.forms)) {
    return data.forms[0]; // Return the first item
  }
  return;
};

const getContactUs = async ({
  hygraphLocale,
  hygraphSite,
  client,
  stage,
  queryOptions,
}: QueryProps & PreviewProps) => {
  // Hardcoded slug for contact us, need to create it in Hygraph BannerItem Components
  const bannerItemVariables = {
    where: {
      slug: 'contactus',
      sites_some: { name: hygraphSite },
    },
    first: 1,
    stage,
    locale: [hygraphLocale, Locale.Default],
  };
  // TODO: log the error inside DataDog?
  const { data: contactUsData } = await client.query<GetBannerItemQuery>({
    query: GetBannerItemDocument,
    variables: bannerItemVariables,
    ...queryOptions,
  });
  // Add the section filter to render it for about/news
  if (Boolean(contactUsData.bannerItems)) {
    return contactUsData.bannerItems[0]; // Return the first item
  }
  return null;
};
